import React from 'react'
import Typography from 'antd/es/typography'
import { useAppSelector } from '../../redux/hooks'
import { selectThemeStyle } from '../../features/setting/setting.slice'

const { Text: AntText, Title: AntTitle } = Typography

interface CustomTextProps {
  size?: 'sm' | 'base' | 'lg' | 'xl'
  fontWeight?: number
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  type?: 'secondary' | 'success' | 'warning' | 'danger'
}

interface CustomTitleProps {
  level?: 1 | 2 | 3 | 4 | 5
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

export const Text: React.FC<CustomTextProps> = ({
  size = 'base',
  fontWeight,
  children,
  type,
  style = {},
  ...props
}) => {
  const theme = useAppSelector(selectThemeStyle)

  const fontSizeMap = {
    sm: theme.fontSizeSM,
    base: theme.fontSizeBase,
    lg: theme.fontSizeLG,
    xl: theme.fontSizeXL,
  }

  return (
    <AntText
      type={type}
      style={{
        fontSize: fontSizeMap[size],
        fontWeight: fontWeight,
        ...style,
      }}
      {...props}
    >
      {children}
    </AntText>
  )
}

export const Title: React.FC<CustomTitleProps> = ({
  level = 1,
  children,
  style = {},
  ...props
}) => {
  const theme = useAppSelector(selectThemeStyle)

  // Map level to the corresponding font size and line height
  const getFontStyles = (level: number) => {
    switch (level) {
      case 1:
        return {
          fontSize: theme.fontSizeHeading1,
          lineHeight: theme.lineHeightHeading1,
        }
      case 2:
        return {
          fontSize: theme.fontSizeHeading2,
          lineHeight: theme.lineHeightHeading2,
        }
      case 3:
        return {
          fontSize: theme.fontSizeHeading3,
          lineHeight: theme.lineHeightHeading3,
        }
      case 4:
        return {
          fontSize: theme.fontSizeHeading4,
          lineHeight: theme.lineHeightHeading4,
        }
      case 5:
        return {
          fontSize: theme.fontSizeHeading5,
          lineHeight: theme.lineHeightHeading5,
        }
      default:
        return {
          fontSize: theme.fontSizeHeading1,
          lineHeight: theme.lineHeightHeading1,
        }
    }
  }

  const fontStyles = getFontStyles(level)

  return (
    <AntTitle
      level={level}
      style={{
        ...fontStyles,
        ...style,
      }}
      {...props}
    >
      {children}
    </AntTitle>
  )
}

// Export an object that mimics the original Typography component structure
const IdeyaTypography = {
  Text,
  Title,
}

export default IdeyaTypography
